import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { OrderTable } from '../../orders';
import {
  listActivityLevels,
  listAllergies,
  listBodyConditions,
  listBreeds,
  listCurrentFoodTypes,
  listHealthIssues,
  listPetKinds,
} from '../../pets';
import { ActivityLevelModel } from '../../pets/models/ActivityLevelModel';
import { AllergyModel } from '../../pets/models/AllergyModel';
import { BodyConditionModel } from '../../pets/models/BodyConditionModel';
import { BreedModel } from '../../pets/models/BreedModel';
import { CurrentFoodTypeModel } from '../../pets/models/CurrentFoodTypeModel';
import { HealthIssueModel } from '../../pets/models/HealthIssueModel';
import {
  CustomerLookupResponse,
  CustomerPlanEligibleMeal,
  CustomerPlanResponse,
  CustomMealRequest,
  createCustomerPlan,
  lookupCustomer,
  simulateCustomerPlan,
} from '../services/PlanCRUD';

/**
 * Alta de plan para un cliente, hecha por un agente (FFJDS-1845). El backend
 * construye el plan de verdad y revierte, así que el precio es el real.
 */

/** Solo se vende a particulares en España y Portugal. */
const SELLING_STORES = [
  { id: 1, label: 'España (foodforjoe.es)' },
  { id: 8, label: 'Portugal (foodforjoe.pt)' },
];
const DEFAULT_STORE = 1;

const CAT_PET_KIND_CODE = 'cat';

// "Casera": mismo id que HOMEMADE_FOOD_TYPE_ID en getMealPlanOptions.ts. El
// catálogo del CRM no expone el code, solo el id.
const HOME_COOKED_FOOD_TYPE_ID = 4;

const AUTOMATIC_PERIOD = 28;
const CUSTOM_PERIODS = [14, 21, 28, 35, 42, 49, 56, 63, 70, 84, 98, 112];

type PetState = {
  name: string;
  pet_kind: number | null;
  breed: number | null;
  gender: string;
  age_years: number | null;
  age_months: number | null;
  weight_in_kg: number | null;
  body_condition: number | null;
  activity_level: number | null;
  current_food_type: number | null;
  is_neutered: boolean;
  allergies: number[];
  health_issues: number[];
};

const EMPTY_PET: PetState = {
  name: '',
  pet_kind: null,
  breed: null,
  // Sexo, años y peso vacíos a propósito: por defecto se enviarían sin
  // que nadie los revise. Los meses no: 0 es la respuesta normal.
  gender: '',
  age_years: null,
  age_months: 0,
  weight_in_kg: null,
  body_condition: null,
  activity_level: null,
  current_food_type: null,
  is_neutered: false,
  allergies: [],
  health_issues: [],
};

// Dígitos que caben en un teléfono, prefijo incluido (E.164).
const MAX_PHONE_DIGITS = 15;

// Corta al máximo de dígitos. No valida el número: de eso sabe
// get_standard_phone, en el backend.
function limitPhone(value: string) {
  const allowed = value.replace(/[^\d+\s-]/g, '');
  const digits = allowed.replace(/\D/g, '');

  if (digits.length <= MAX_PHONE_DIGITS) {
    return allowed;
  }
  return allowed.slice(0, -1);
}

// Con serializers anidados el error también lo está; sin aplanar sale
// "customer: [object Object]".
function flattenErrors(detail: any, path = ''): string[] {
  if (detail === null || detail === undefined) {
    return [];
  }
  if (Array.isArray(detail)) {
    return detail.flatMap((item) => flattenErrors(item, path));
  }
  if (typeof detail === 'object') {
    return Object.entries(detail).flatMap(([field, value]) =>
      flattenErrors(value, path ? `${path}.${field}` : field)
    );
  }
  return [path ? `${path}: ${detail}` : String(detail)];
}

export default function CustomerPlanForm() {
  const intl = useIntl();
  const location = useLocation();

  const [petKinds, setPetKinds] = useState<Array<any>>([]);
  const [breeds, setBreeds] = useState<Array<BreedModel>>([]);
  const [bodyConditions, setBodyConditions] = useState<
    Array<BodyConditionModel>
  >([]);
  const [activityLevels, setActivityLevels] = useState<
    Array<ActivityLevelModel>
  >([]);
  const [currentFoodTypes, setCurrentFoodTypes] = useState<
    Array<CurrentFoodTypeModel>
  >([]);
  const [allergies, setAllergies] = useState<Array<AllergyModel>>([]);
  const [healthIssues, setHealthIssues] = useState<Array<HealthIssueModel>>([]);

  // --- cliente ---
  // null mientras el agente no ha contestado si el cliente ya existe. De esa
  // respuesta cuelga todo lo demás.
  const [isExistingCustomer, setIsExistingCustomer] = useState<boolean | null>(
    null
  );
  const [email, setEmail] = useState('');
  const [lookup, setLookup] = useState<CustomerLookupResponse | undefined>();
  const [lookingUp, setLookingUp] = useState(false);
  // Dijo "es nuevo" pero el email ya existe. Se puede crear, pero al
  // precio simulado le faltarían su tienda y el -10 % de segunda mascota.
  const [emailAlreadyRegistered, setEmailAlreadyRegistered] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [store, setStore] = useState<number>(DEFAULT_STORE);

  // --- mascota y plan ---
  const [pet, setPet] = useState<PetState>(EMPTY_PET);
  const [kind, setKind] = useState<'automatic' | 'custom'>('automatic');
  const [period, setPeriod] = useState<number>(AUTOMATIC_PERIOD);
  const [combine, setCombine] = useState(false);
  const [withTrial, setWithTrial] = useState(true);
  const [customMeals, setCustomMeals] = useState<Array<CustomMealRequest>>([]);
  const [eligibleMeals, setEligibleMeals] = useState<
    Array<CustomerPlanEligibleMeal>
  >([]);
  const [loadingMeals, setLoadingMeals] = useState(false);

  const [simulating, setSimulating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [simulation, setSimulation] = useState<
    CustomerPlanResponse | undefined
  >();
  const [created, setCreated] = useState<CustomerPlanResponse | undefined>();

  const customerResolved =
    isExistingCustomer === false ||
    (isExistingCustomer === true && !!lookup?.found);
  const customerExists = !!lookup?.found;
  const isCat =
    petKinds.find((petKind) => petKind.id === pet.pet_kind)?.code ===
    CAT_PET_KIND_CODE;
  const isCustom = kind === 'custom';
  const eatsHomemadeFood = pet.current_food_type === HOME_COOKED_FOOD_TYPE_ID;

  useEffect(() => {
    async function fetchCatalogs() {
      setPetKinds((await listPetKinds()).data);
    }
    fetchCatalogs();
  }, []);

  useEffect(() => {
    async function fetchFormOptions(petKind: number) {
      setBreeds((await listBreeds({ pet_kind: petKind })).data);
      setBodyConditions((await listBodyConditions({ pet_kind: petKind })).data);
      setActivityLevels((await listActivityLevels({ pet_kind: petKind })).data);
      setCurrentFoodTypes(
        (await listCurrentFoodTypes({ pet_kind: petKind })).data
      );
      setAllergies((await listAllergies({ pet_kind: petKind })).data);
      setHealthIssues((await listHealthIssues({ pet_kind: petKind })).data);
    }
    if (pet.pet_kind) {
      fetchFormOptions(pet.pet_kind);
    }
  }, [pet.pet_kind]);

  // El backend prohíbe la ración complementaria en gatos.
  useEffect(() => {
    if (isCat && combine) {
      setCombine(false);
    }
  }, [isCat]);

  // En automático son siempre 28 días; el backend los sube si el envío
  // no llega a 3 kg.
  useEffect(() => {
    if (!isCustom) {
      setPeriod(AUTOMATIC_PERIOD);
    }
  }, [isCustom]);

  // Cualquier cambio invalida la simulación: los números los calcula el
  // servidor, y una simulación vieja es un precio que ya no existe.
  useEffect(() => {
    setSimulation(undefined);
  }, [pet, kind, period, combine, withTrial, customMeals, store, lookup]);

  function reportError(error: any) {
    const messages = flattenErrors(error?.response?.data);
    toast.error(messages.join(' · ') || error?.message);
  }

  /**
   * Cambiar de especie invalida los catálogos y las recetas de un plan
   * custom, que el backend rechaza. Vuelve a automático.
   */
  function onPetKindChange(petKindId: number) {
    setPet({
      ...pet,
      pet_kind: petKindId,
      breed: null,
      body_condition: null,
      activity_level: null,
      current_food_type: null,
      allergies: [],
      health_issues: [],
    });
    setKind('automatic');
    setCustomMeals([]);
    setEligibleMeals([]);
  }

  // Los datos de un cliente no valen para el otro.
  function onIsExistingCustomerChange(value: string) {
    setIsExistingCustomer(value === '' ? null : value === 'true');
    setLookup(undefined);
    setEmail('');
    setCustomerName('');
    setCustomerPhone('');
    setStore(DEFAULT_STORE);
    setEmailAlreadyRegistered(false);
    setSimulation(undefined);
  }

  /**
   * El email tecleado al final puede ser de alguien que ya es cliente: el
   * lookup lo detecta y carga su tienda, que es la que cambia el precio.
   */
  async function adoptCustomerIfRegistered(value: string) {
    if (!value || isExistingCustomer !== false) {
      return;
    }
    try {
      const { data } = await lookupCustomer(value);
      setEmailAlreadyRegistered(!!data.found);
      if (!data.found) {
        return;
      }
      setIsExistingCustomer(true);
      setLookup(data);
      setCustomerName(data.name || '');
      setCustomerPhone(data.phone || '');
      setStore(data.store);
    } catch {
      setEmailAlreadyRegistered(false);
    }
  }

  async function lookupEmail(customerEmail: string) {
    if (!customerEmail) {
      return;
    }
    try {
      setLookingUp(true);
      const { data } = await lookupCustomer(customerEmail);
      setLookup(data);
      if (data.found) {
        // Los datos del cliente mandan sobre lo que el agente haya escrito.
        setCustomerName(data.name || '');
        setCustomerPhone(data.phone || '');
        setStore(data.store);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLookingUp(false);
    }
  }

  function onLookup() {
    lookupEmail(email);
  }

  // "+ Nueva mascota" on the quick-sale page opens this form with the
  // customer's email, already looked up.
  useEffect(function prefillCustomerFromUrl() {
    const prefilledEmail = new URLSearchParams(location.search).get('email');
    if (!prefilledEmail) {
      return;
    }
    setIsExistingCustomer(true);
    setEmail(prefilledEmail);
    lookupEmail(prefilledEmail);
    // Only on mount: the URL is the entry point, not a source of truth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onEmailChange(value: string) {
    setEmail(value);
    setEmailAlreadyRegistered(false);
    // Cambiar el email obliga a volver a buscar.
    if (lookup) {
      setLookup(undefined);
      setCustomerName('');
      setCustomerPhone('');
      setStore(DEFAULT_STORE);
    }
  }

  /**
   * Los datos del cliente solo viajan si el lookup lo reconoció. Los de uno
   * nuevo se teclean al final; sin ellos el backend simula con uno de usar
   * y tirar.
   */
  function buildRequest(forCreate: boolean) {
    const sendCustomer = forCreate || customerExists;

    return {
      store,
      pet: {
        ...pet,
        weight_in_kg: pet.weight_in_kg,
        age_years: pet.age_years,
        age_months: pet.age_months,
      },
      period: isCustom ? period : null,
      combine: isCat ? false : combine,
      kind,
      with_trial: withTrial,
      ...(isCustom ? { meals: customMeals } : {}),
      ...(sendCustomer && email
        ? {
            customer: {
              email,
              name: customerName,
              phone: customerPhone,
            },
          }
        : {}),
    };
  }

  async function onSimulate() {
    try {
      setSimulating(true);
      setCreated(undefined);
      const { data } = await simulateCustomerPlan(buildRequest(false));
      setSimulation(data);
      setEligibleMeals(data.eligible_meals || []);
    } catch (error) {
      reportError(error);
    } finally {
      setSimulating(false);
    }
  }

  /**
   * Las recetas que el plan admite solo las sabe el backend una vez
   * construido, así que se pide una simulación, que es gratis: se revierte.
   */
  async function loadEligibleMeals() {
    try {
      setLoadingMeals(true);
      const { data } = await simulateCustomerPlan({
        ...buildRequest(false),
        kind: 'automatic',
        period: null,
        meals: [],
      });
      setEligibleMeals(data.eligible_meals || []);
      if (!customMeals.length && data.eligible_meals?.length) {
        setCustomMeals(
          data.eligible_meals.slice(0, 2).map((meal) => ({
            meal: meal.id,
            meal_size: meal.sizes[meal.sizes.length - 1]?.id,
            units: 4,
          }))
        );
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoadingMeals(false);
    }
  }

  async function onKindChange(value: 'automatic' | 'custom') {
    setKind(value);
    if (value === 'custom' && !eligibleMeals.length) {
      await loadEligibleMeals();
    }
  }

  async function onCreate() {
    try {
      setCreating(true);
      const { data } = await createCustomerPlan(buildRequest(true));
      setCreated(data);
      toast.success(intl.formatMessage({ id: 'customerPlan.created' }));
    } catch (error) {
      reportError(error);
    } finally {
      setCreating(false);
    }
  }

  const result = simulation;
  const trialOrder = result?.orders?.find((order) => order.is_trial);
  const regularOrder = result?.orders?.find((order) => !order.is_trial);

  function formatPrice(price?: number) {
    if (price === undefined || price === null) {
      return '-';
    }
    return intl.formatNumber(price, { style: 'currency', currency: 'EUR' });
  }

  const petIsComplete =
    !!pet.name &&
    !!pet.pet_kind &&
    !!pet.breed &&
    !!pet.gender &&
    pet.age_years !== null &&
    pet.age_months !== null &&
    pet.weight_in_kg !== null &&
    !!pet.body_condition &&
    !!pet.activity_level &&
    !!pet.current_food_type;

  const canSimulate =
    customerResolved && petIsComplete && (!isCustom || customMeals.length > 0);

  const contactIsComplete = customerExists
    ? !!email
    : !!email && !!customerName && !!customerPhone;
  const canCreate = canSimulate && contactIsComplete;

  if (created) {
    return (
      <CreatedPlan
        plan={created}
        onNewPlan={() => {
          setCreated(undefined);
          setSimulation(undefined);
          setPet(EMPTY_PET);
          setCustomMeals([]);
          setEligibleMeals([]);
        }}
      />
    );
  }

  return (
    <>
      {/* 1. De quién es el plan. Va primero porque los datos de un cliente
          conocido cambian el precio (su tienda, sus otras mascotas). */}
      <section className="card p-10">
        <h1 className="mb-8">
          {intl.formatMessage({ id: 'customerPlan.customer' })}
        </h1>

        <div className="row">
          <div className="col-12 col-lg-5 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.isExistingCustomer' })}
            </label>
            <select
              className="form-select"
              value={
                isExistingCustomer === null ? '' : String(isExistingCustomer)
              }
              onChange={(event) =>
                onIsExistingCustomerChange(event.target.value)
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'genericForm.selectPlaceholder' })}
              </option>
              <option value="true">
                {intl.formatMessage({ id: 'customerPlan.yesExisting' })}
              </option>
              <option value="false">
                {intl.formatMessage({ id: 'customerPlan.noExisting' })}
              </option>
            </select>
          </div>

          {/* Cliente conocido: su email primero, porque sus datos mandan. */}
          {isExistingCustomer === true && (
            <div className="col-12 col-lg-7 mb-5">
              <label className="fs-6 fw-bold mb-2">
                {intl.formatMessage({ id: 'customerPlan.customerEmail' })}
              </label>
              <div className="d-flex gap-3">
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(event) => onEmailChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      onLookup();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-light-primary text-nowrap"
                  disabled={!email || lookingUp}
                  onClick={onLookup}
                >
                  {lookingUp
                    ? intl.formatMessage({ id: 'customerPlan.searching' })
                    : intl.formatMessage({ id: 'customerPlan.search' })}
                </button>
              </div>
            </div>
          )}

          {/* Cliente nuevo: nada personal todavía, solo dónde vive, que fija
              la tienda y con ella el IVA. Se pregunta por la residencia y no
              por la tienda porque es lo que el agente sabe contestar. */}
          {isExistingCustomer === false && (
            <div className="col-12 col-lg-5 mb-5">
              <label className="fs-6 fw-bold mb-2">
                {intl.formatMessage({ id: 'customerPlan.livesIn' })}
              </label>
              <select
                className="form-select"
                value={store}
                onChange={(event) => setStore(parseInt(event.target.value, 10))}
              >
                {SELLING_STORES.map((sellingStore) => (
                  <option key={sellingStore.id} value={sellingStore.id}>
                    {sellingStore.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {isExistingCustomer === true && customerExists && (
          <>
            <div className="alert alert-primary">
              <strong>
                {intl.formatMessage({ id: 'customerPlan.existingCustomer' })}
              </strong>{' '}
              {intl.formatMessage({ id: 'customerPlan.existingCustomerHelp' })}
              {/* La pantalla se reconfiguró sola al reconocer el email
                  escrito abajo: sin esto el agente no sabría por qué. */}
              {emailAlreadyRegistered && (
                <div className="mt-2">
                  {intl.formatMessage({ id: 'customerPlan.emailRegistered' })}
                </div>
              )}
              {lookup?.has_plan && (
                <div className="mt-2">
                  {intl.formatMessage({ id: 'customerPlan.secondPetDiscount' })}
                </div>
              )}
            </div>
            <CustomerFields
              email={email}
              name={customerName}
              phone={customerPhone}
              store={store}
              lookupStoreName={lookup?.store_name}
              locked
              onNameChange={setCustomerName}
              onPhoneChange={setCustomerPhone}
              onStoreChange={setStore}
            />
          </>
        )}

        {isExistingCustomer === true && lookup && !lookup.found && (
          <div className="bg-light-warning text-gray-800 rounded p-3 fs-7 d-flex gap-3">
            <span className="fas fa-exclamation-triangle text-warning mt-1" />
            <span>{intl.formatMessage({ id: 'customerPlan.notFound' })}</span>
          </div>
        )}
      </section>

      {/* 2. Mascota. Bloqueada hasta saber de quién es. */}
      <section
        className={clsx('card p-10 mt-5', !customerResolved && 'opacity-50')}
      >
        <h1 className="mb-8">
          {intl.formatMessage({ id: 'customerPlan.pet' })}
        </h1>
        {!customerResolved && (
          <p className="text-muted">
            {intl.formatMessage({ id: 'customerPlan.resolveEmailFirst' })}
          </p>
        )}

        <fieldset disabled={!customerResolved} className="row">
          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.petKind' })}
            </label>
            <select
              className="form-select"
              value={pet.pet_kind ?? ''}
              onChange={(event) =>
                onPetKindChange(parseInt(event.target.value, 10))
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'genericForm.selectPlaceholder' })}
              </option>
              {petKinds.map((petKind) => (
                <option key={petKind.id} value={petKind.id}>
                  {petKind.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.petName' })}
            </label>
            <input
              type="text"
              className="form-control"
              value={pet.name}
              onChange={(event) => setPet({ ...pet, name: event.target.value })}
            />
          </div>

          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.gender' })}
            </label>
            <select
              className="form-select"
              value={pet.gender}
              onChange={(event) =>
                setPet({ ...pet, gender: event.target.value })
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'genericForm.selectPlaceholder' })}
              </option>
              <option value="male">
                {intl.formatMessage({ id: 'petProfile.male' })}
              </option>
              <option value="female">
                {intl.formatMessage({ id: 'petProfile.female' })}
              </option>
            </select>
          </div>

          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.breed' })}
            </label>
            <select
              className="form-select"
              value={pet.breed ?? ''}
              onChange={(event) =>
                setPet({ ...pet, breed: parseInt(event.target.value, 10) })
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'genericForm.selectPlaceholder' })}
              </option>
              {breeds.map((breed) => (
                <option key={breed.id} value={breed.id}>
                  {breed.name}
                </option>
              ))}
            </select>
          </div>

          {/* Años y meses en el MISMO bloque de rejilla: ninguna anchura de
              pantalla puede separarlos. */}
          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.age' })}
            </label>
            <div className="input-group" style={{ maxWidth: '240px' }}>
              <NumberInput
                value={pet.age_years}
                onChange={(value) => setPet({ ...pet, age_years: value })}
                maxWidth="56px"
              />
              <span className="input-group-text">
                {intl.formatMessage({ id: 'customerPlan.years' })}
              </span>
              <NumberInput
                value={pet.age_months}
                onChange={(value) => setPet({ ...pet, age_months: value })}
                maxWidth="56px"
                max={11}
              />
              <span className="input-group-text">
                {intl.formatMessage({ id: 'customerPlan.months' })}
              </span>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.weight' })}
            </label>
            <NumberInput
              value={pet.weight_in_kg}
              onChange={(value) => setPet({ ...pet, weight_in_kg: value })}
              decimals
              maxWidth="120px"
            />
          </div>

          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.bodyCondition' })}
            </label>
            <select
              className="form-select"
              value={pet.body_condition ?? ''}
              onChange={(event) =>
                setPet({
                  ...pet,
                  body_condition: parseInt(event.target.value, 10),
                })
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'genericForm.selectPlaceholder' })}
              </option>
              {bodyConditions.map((bodyCondition) => (
                <option key={bodyCondition.id} value={bodyCondition.id}>
                  {bodyCondition.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.activityLevel' })}
            </label>
            <select
              className="form-select"
              value={pet.activity_level ?? ''}
              onChange={(event) =>
                setPet({
                  ...pet,
                  activity_level: parseInt(event.target.value, 10),
                })
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'genericForm.selectPlaceholder' })}
              </option>
              {activityLevels.map((activityLevel) => (
                <option key={activityLevel.id} value={activityLevel.id}>
                  {activityLevel.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.currentFoodType' })}
            </label>
            <select
              className="form-select"
              value={pet.current_food_type ?? ''}
              onChange={(event) =>
                setPet({
                  ...pet,
                  current_food_type: parseInt(event.target.value, 10),
                })
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'genericForm.selectPlaceholder' })}
              </option>
              {currentFoodTypes.map((currentFoodType) => (
                <option key={currentFoodType.id} value={currentFoodType.id}>
                  {currentFoodType.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.isNeutered' })}
            </label>
            <div className="mt-3">
              <Toggle
                checked={pet.is_neutered}
                onChange={() =>
                  setPet({ ...pet, is_neutered: !pet.is_neutered })
                }
              />
            </div>
          </div>

          {/* Fila propia: detrás de los campos sueltos el grid las parte. */}
          <div className="col-12">
            <div className="row">
              <div className="col-12 col-lg-6 mb-5">
                <label className="fs-6 fw-bold mb-2">
                  {intl.formatMessage({ id: 'customerPlan.allergies' })}
                </label>
                <CheckboxList
                  options={allergies}
                  selected={pet.allergies}
                  onChange={(selected) =>
                    setPet({ ...pet, allergies: selected })
                  }
                />
              </div>

              <div className="col-12 col-lg-6 mb-5">
                <label className="fs-6 fw-bold mb-2">
                  {intl.formatMessage({ id: 'customerPlan.healthIssues' })}
                </label>
                <CheckboxList
                  options={healthIssues}
                  selected={pet.health_issues}
                  onChange={(selected) =>
                    setPet({ ...pet, health_issues: selected })
                  }
                />
              </div>
            </div>
          </div>
        </fieldset>
      </section>

      {/* 3. Plan. */}
      <section
        className={clsx('card p-10 mt-5', !customerResolved && 'opacity-50')}
      >
        <h1 className="mb-8">
          {intl.formatMessage({ id: 'customerPlan.plan' })}
        </h1>

        <fieldset disabled={!customerResolved} className="row">
          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.kind' })}
            </label>
            <select
              className="form-select"
              value={kind}
              onChange={(event) =>
                onKindChange(event.target.value as 'automatic' | 'custom')
              }
            >
              <option value="automatic">
                {intl.formatMessage({ id: 'customerPlan.kindAutomatic' })}
              </option>
              <option value="custom">
                {intl.formatMessage({ id: 'customerPlan.kindCustom' })}
              </option>
            </select>
          </div>

          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.period' })}
            </label>
            <select
              className="form-select"
              disabled={!isCustom}
              value={period}
              onChange={(event) => setPeriod(parseInt(event.target.value, 10))}
            >
              {(isCustom ? CUSTOM_PERIODS : [AUTOMATIC_PERIOD]).map((days) => (
                <option key={days} value={days}>
                  {days}
                </option>
              ))}
            </select>
            {!isCustom && (
              <div className="text-gray-700 fs-7 mt-2">
                {intl.formatMessage({ id: 'customerPlan.periodAutomaticHelp' })}
              </div>
            )}
          </div>

          <div className="col-12 col-sm-6 col-xl-3 mb-5">
            <label className="fs-6 fw-bold mb-2">
              {intl.formatMessage({ id: 'customerPlan.withTrial' })}
            </label>
            <div className="text-gray-700 fs-7 mb-2">
              {intl.formatMessage({ id: 'customerPlan.withTrialHelp' })}
            </div>
            <Toggle
              checked={withTrial}
              onChange={() => setWithTrial(!withTrial)}
            />
            {/* El quiz solo deja saltarse la transición a quien ya da comida
                cocinada. Aquí decide el agente, pero que lo sepa. */}
            {!withTrial && !eatsHomemadeFood && (
              <div className="bg-light-warning text-gray-800 rounded p-3 fs-7 mt-3 d-flex gap-3">
                <span className="fas fa-exclamation-triangle text-warning mt-1" />
                <span>
                  {intl.formatMessage({ id: 'customerPlan.withTrialWarning' })}
                </span>
              </div>
            )}
          </div>

          {/* Prohibida para gatos en el backend: no se ofrece siquiera. */}
          {!isCat && (
            <div className="col-12 col-sm-6 col-xl-3 mb-5">
              <label className="fs-6 fw-bold mb-2">
                {intl.formatMessage({ id: 'customerPlan.combine' })}
              </label>
              <div className="text-gray-700 fs-7 mb-2">
                {intl.formatMessage({ id: 'customerPlan.combineHelp' })}
              </div>
              <Toggle checked={combine} onChange={() => setCombine(!combine)} />
            </div>
          )}
        </fieldset>

        {isCustom && (
          <CustomMealsEditor
            meals={customMeals}
            eligibleMeals={eligibleMeals}
            loading={loadingMeals}
            onChange={setCustomMeals}
            onReload={loadEligibleMeals}
          />
        )}

        <div className="d-flex justify-content-end gap-3 mt-5">
          <button
            type="button"
            className="btn btn-light-primary"
            disabled={!canSimulate || simulating}
            onClick={onSimulate}
          >
            {simulating
              ? intl.formatMessage({ id: 'customerPlan.simulating' })
              : intl.formatMessage({ id: 'customerPlan.simulate' })}
          </button>
        </div>
      </section>

      {result && (
        <section className="mt-5 card">
          <div className="card-body">
            <h2 className="mb-5">
              {intl.formatMessage({ id: 'customerPlan.results' })}
            </h2>

            {result.period_was_adjusted && (
              <div className="alert alert-warning">
                {intl.formatMessage(
                  { id: 'customerPlan.periodAdjusted' },
                  {
                    requested: result.requested_period_days,
                    real: result.period_days,
                  }
                )}
              </div>
            )}

            <div className="row mb-8">
              <ResultValue
                label={intl.formatMessage({ id: 'customerPlan.trialCharge' })}
                value={formatPrice(trialOrder?.total_price_inc_tax)}
              />
              <ResultValue
                label={intl.formatMessage({ id: 'customerPlan.regularCharge' })}
                value={formatPrice(regularOrder?.total_price_inc_tax)}
              />
              <ResultValue
                label={intl.formatMessage({ id: 'customerPlan.periodDays' })}
                value={`${result.period_days ?? '-'}`}
              />
              <ResultValue
                label={intl.formatMessage({
                  id: 'customerPlan.averageGramsPerDay',
                })}
                value={`${result.average_grams_per_day} g`}
              />
              <ResultValue
                label={intl.formatMessage({
                  id: 'customerPlan.dailyCalorieIntake',
                })}
                value={`${result.daily_calorie_intake} kcal`}
              />
              <ResultValue
                label={intl.formatMessage({
                  id: 'customerPlan.maxDaysInFreezer',
                })}
                value={`${result.max_days_in_freezer}`}
              />
            </div>

            <h3 className="mb-3">
              {intl.formatMessage({ id: 'customerPlan.meals' })}
            </h3>
            <div className="table-responsive mb-8">
              <table className="table table-row-bordered align-middle">
                <thead>
                  <tr className="fw-bold">
                    <th>{intl.formatMessage({ id: 'customerPlan.meal' })}</th>
                    <th>{intl.formatMessage({ id: 'customerPlan.days' })}</th>
                    <th>{intl.formatMessage({ id: 'customerPlan.grams' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.meals.map((meal) => (
                    <tr key={meal.meal_id}>
                      <td>{meal.meal_name}</td>
                      <td>{meal.days}</td>
                      <td>{meal.weight_in_gr} g</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="mb-3">
              {intl.formatMessage({ id: 'customerPlan.orders' })}
            </h3>
            <p className="text-muted">
              {intl.formatMessage({ id: 'customerPlan.ordersHelp' })}
            </p>
            <div className="mb-8">
              <OrderTable
                orders={result.orders}
                page={1}
                // Sin paginación: son dos o tres pedidos, no un listado.
                count={0}
                displayPetName={false}
                isSimulation={result.is_simulation}
                onPageChange={() => undefined}
                onRefresh={() => undefined}
                emptyMessage={intl.formatMessage({
                  id: 'customerPlan.noOrders',
                })}
              />
            </div>

            {/* Los datos del cliente nuevo se piden aquí y no antes: primero
                se negocia el precio. Los de uno reconocido ya están arriba. */}
            {!customerExists && (
              <div className="border-top pt-8 mt-4">
                <h3 className="mb-3">
                  {intl.formatMessage({ id: 'customerPlan.contactTitle' })}
                </h3>
                <p className="text-gray-700 fs-7">
                  {intl.formatMessage({ id: 'customerPlan.contactHelp' })}
                </p>
                <CustomerFields
                  email={email}
                  name={customerName}
                  phone={customerPhone}
                  store={store}
                  onEmailBlur={adoptCustomerIfRegistered}
                  onEmailChange={setEmail}
                  onNameChange={setCustomerName}
                  onPhoneChange={setCustomerPhone}
                />
              </div>
            )}

            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canCreate || creating}
                onClick={onCreate}
              >
                {creating
                  ? intl.formatMessage({ id: 'customerPlan.creating' })
                  : intl.formatMessage({ id: 'customerPlan.create' })}
              </button>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

// Nombre, teléfono y tienda. Bloqueados si el email reconoció al cliente,
// editables justo antes de crear si es nuevo.
function CustomerFields({
  email,
  name,
  phone,
  store,
  lookupStoreName,
  locked = false,
  onEmailChange,
  onEmailBlur,
  onNameChange,
  onPhoneChange,
  onStoreChange,
}: {
  email: string;
  name: string;
  phone: string;
  store: number;
  lookupStoreName?: string;
  locked?: boolean;
  onEmailChange?: (value: string) => void;
  onEmailBlur?: (value: string) => void;
  onNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onStoreChange?: (value: number) => void;
}) {
  const intl = useIntl();

  return (
    <div className="row">
      {onEmailChange && (
        <div className="col-12 col-sm-6 col-xl-3 mb-5">
          <label className="fs-6 fw-bold mb-2">
            {intl.formatMessage({ id: 'customerPlan.customerEmail' })}
          </label>
          <input
            type="email"
            className="form-control"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            onBlur={(event) => onEmailBlur?.(event.target.value)}
          />
        </div>
      )}
      <div className="col-12 col-sm-6 col-xl-3 mb-5">
        <label className="fs-6 fw-bold mb-2">
          {intl.formatMessage({ id: 'customerPlan.customerName' })}
        </label>
        <input
          type="text"
          className="form-control"
          disabled={locked}
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
        />
      </div>
      <div className="col-12 col-sm-6 col-xl-3 mb-5">
        <label className="fs-6 fw-bold mb-2">
          {intl.formatMessage({ id: 'customerPlan.customerPhone' })}
        </label>
        <input
          type="tel"
          className="form-control"
          disabled={locked}
          value={phone}
          onChange={(event) => onPhoneChange(limitPhone(event.target.value))}
        />
      </div>
      {onStoreChange && (
        <div className="col-12 col-sm-6 col-xl-3 mb-5">
          <label className="fs-6 fw-bold mb-2">
            {intl.formatMessage({ id: 'customerPlan.store' })}
          </label>
          <select
            className="form-select"
            disabled={locked}
            value={store}
            onChange={(event) =>
              onStoreChange(parseInt(event.target.value, 10))
            }
          >
            {SELLING_STORES.map((sellingStore) => (
              <option key={sellingStore.id} value={sellingStore.id}>
                {sellingStore.label}
              </option>
            ))}
            {/* Un cliente antiguo puede ser de una tienda que ya no se
              ofrece: se muestra igual. */}
            {locked && !SELLING_STORES.some((item) => item.id === store) && (
              <option value={store}>{lookupStoreName}</option>
            )}
          </select>
          {locked && (
            <div className="text-gray-700 fs-7 mt-2">
              {intl.formatMessage({ id: 'customerPlan.storeLocked' })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Sustituye al formulario una vez creado el plan: a estas alturas lo que hace
// falta es el enlace de pago.
function CreatedPlan({
  plan,
  onNewPlan,
}: {
  plan: CustomerPlanResponse;
  onNewPlan: () => void;
}) {
  const intl = useIntl();

  function copyCheckoutUrl() {
    if (!plan.checkout_url) {
      return;
    }
    try {
      if (navigator?.clipboard) {
        navigator.clipboard.writeText(plan.checkout_url);
      } else {
        // Sin HTTPS no hay API de portapapeles. Pasa en local, que va por HTTP.
        const field = document.createElement('textarea');
        field.value = plan.checkout_url;
        document.body.appendChild(field);
        field.select();
        document.execCommand('copy');
        field.remove();
      }
      toast.success(intl.formatMessage({ id: 'customerPlan.linkCopied' }));
    } catch (error) {
      console.warn({ error });
      toast.error(intl.formatMessage({ id: 'customerPlan.linkCopyError' }));
    }
  }

  return (
    <section className="card border border-2 border-success">
      <div className="card-body p-10">
        <div className="d-flex justify-content-between align-items-start mb-5">
          <h2 className="mb-0">
            {intl.formatMessage({ id: 'customerPlan.createdTitle' })}
          </h2>
          <button
            type="button"
            className="btn btn-light-primary btn-sm"
            onClick={onNewPlan}
          >
            {intl.formatMessage({ id: 'customerPlan.newPlan' })}
          </button>
        </div>

        <p>
          <strong>
            {intl.formatMessage({ id: 'customerPlan.planCode' })}:
          </strong>{' '}
          {plan.code}
          {plan.customer_created && (
            <span className="badge badge-light-primary ms-3">
              {intl.formatMessage({ id: 'customerPlan.newCustomer' })}
            </span>
          )}
        </p>

        <div className="mt-5">
          <label className="fs-6 fw-bold mb-2">
            {intl.formatMessage({ id: 'customerPlan.checkoutUrl' })}
          </label>
          <p className="text-gray-700 fs-7">
            {intl.formatMessage({ id: 'customerPlan.checkoutUrlHelp' })}
          </p>
          <div className="d-flex align-items-center">
            <input
              className="form-control me-3"
              readOnly
              value={plan.checkout_url ?? ''}
            />
            <button
              type="button"
              className="btn btn-primary text-nowrap"
              onClick={copyCheckoutUrl}
            >
              {intl.formatMessage({ id: 'customerPlan.copyLink' })}
            </button>
          </div>
        </div>

        <div className="mt-8">
          <a
            href={`/user/${plan.customer_id}/plan/${plan.id}`}
            target="_blank"
            rel="noreferrer"
            className="btn btn-primary"
          >
            {intl.formatMessage({ id: 'customerPlan.openPlan' })}
          </a>
        </div>
      </div>
    </section>
  );
}

// Con el estado escrito: el switch a secas se distingue solo por el color.
function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  const intl = useIntl();

  return (
    <label className="form-check form-switch form-check-custom form-check-solid d-flex align-items-center gap-3 cursor-pointer">
      <input
        type="checkbox"
        className="form-check-input"
        checked={checked}
        onChange={onChange}
      />
      <span
        className={clsx('fw-bold', checked ? 'text-primary' : 'text-muted')}
      >
        {checked
          ? intl.formatMessage({ id: 'genericForm.yes' })
          : intl.formatMessage({ id: 'genericForm.no' })}
      </span>
    </label>
  );
}

/**
 * <input type="number"> deja teclear letras y devuelve cadena vacía: el
 * estado queda a nulo mientras en pantalla se sigue leyendo lo escrito.
 */
function NumberInput({
  value,
  onChange,
  decimals = false,
  maxWidth,
  max,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  decimals?: boolean;
  maxWidth?: string;
  max?: number;
}) {
  const pattern = decimals ? /^\d*[.,]?\d*$/ : /^\d*$/;

  return (
    <input
      type="text"
      inputMode={decimals ? 'decimal' : 'numeric'}
      className="form-control"
      style={maxWidth ? { maxWidth } : undefined}
      value={value ?? ''}
      onChange={(event) => {
        const raw = event.target.value;
        if (!pattern.test(raw)) {
          return;
        }
        if (raw === '') {
          onChange(null);
          return;
        }
        const parsed = Number(raw.replace(',', '.'));
        if (Number.isNaN(parsed)) {
          onChange(null);
          return;
        }
        // Se recorta en vez de rechazar la tecla, para que se vea el límite.
        onChange(max !== undefined ? Math.min(parsed, max) : parsed);
      }}
    />
  );
}

function CheckboxList({
  options,
  selected,
  onChange,
}: {
  options: Array<{ id: number; name: string }>;
  selected: number[];
  onChange: (selected: number[]) => void;
}) {
  return (
    <div
      className="border rounded p-3 bg-white"
      style={{ maxHeight: '220px', overflowY: 'auto' }}
    >
      {options.map((option) => (
        <label
          className="form-check-label mt-2 d-block cursor-pointer"
          key={option.id}
        >
          <input
            type="checkbox"
            className="form-check-input me-4"
            checked={selected.includes(option.id)}
            onChange={(event) =>
              onChange(
                event.target.checked
                  ? [...selected, option.id]
                  : selected.filter((id) => id !== option.id)
              )
            }
          />
          {option.name}
        </label>
      ))}
    </div>
  );
}

/**
 * Los formatos salen de eligible_meals, que el backend calcula con las mismas
 * reglas que aplicará al guardar. CreateCustomMealSerializer no las aplica.
 */
function CustomMealsEditor({
  meals,
  eligibleMeals,
  loading,
  onChange,
  onReload,
}: {
  meals: Array<CustomMealRequest>;
  eligibleMeals: Array<CustomerPlanEligibleMeal>;
  loading: boolean;
  onChange: (meals: Array<CustomMealRequest>) => void;
  onReload: () => void;
}) {
  const intl = useIntl();

  function sizesFor(mealId: number) {
    return eligibleMeals.find((meal) => meal.id === mealId)?.sizes ?? [];
  }

  function updateLine(index: number, changes: Partial<CustomMealRequest>) {
    onChange(
      meals.map((line, i) => (i === index ? { ...line, ...changes } : line))
    );
  }

  const totalGrams = meals.reduce((total, line) => {
    const size = sizesFor(line.meal).find((item) => item.id === line.meal_size);
    return total + (size?.weight_in_gr ?? 0) * line.units;
  }, 0);

  if (loading) {
    return (
      <div className="mt-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (!eligibleMeals.length) {
    return (
      <div className="alert alert-warning mt-5 d-flex justify-content-between align-items-center">
        <span>
          {intl.formatMessage({ id: 'customerPlan.noEligibleMeals' })}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={onReload}
        >
          {intl.formatMessage({ id: 'customerPlan.loadMeals' })}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <h3 className="mb-3">
        {intl.formatMessage({ id: 'customerPlan.customMeals' })}
      </h3>
      <div className="table-responsive">
        <table className="table table-row-bordered align-middle">
          <thead>
            <tr className="fw-bold">
              <th>{intl.formatMessage({ id: 'customerPlan.meal' })}</th>
              <th className="w-150px">
                {intl.formatMessage({ id: 'customerPlan.mealSize' })}
              </th>
              <th className="w-125px text-center">
                {intl.formatMessage({ id: 'customerPlan.units' })}
              </th>
              <th className="w-100px text-end">
                {intl.formatMessage({ id: 'customerPlan.grams' })}
              </th>
              <th className="w-50px" />
            </tr>
          </thead>
          <tbody>
            {meals.map((line, index) => {
              const sizes = sizesFor(line.meal);
              const size = sizes.find((item) => item.id === line.meal_size);
              return (
                <tr key={index}>
                  <td>
                    <select
                      className="form-select"
                      value={line.meal}
                      onChange={(event) => {
                        const mealId = parseInt(event.target.value, 10);
                        const available = sizesFor(mealId);
                        updateLine(index, {
                          meal: mealId,
                          // Puede no existir ese formato para la receta nueva.
                          meal_size: available[available.length - 1]?.id,
                        });
                      }}
                    >
                      {eligibleMeals.map((meal) => (
                        <option key={meal.id} value={meal.id}>
                          {meal.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="form-select"
                      value={line.meal_size}
                      onChange={(event) =>
                        updateLine(index, {
                          meal_size: parseInt(event.target.value, 10),
                        })
                      }
                    >
                      {sizes.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.weight_in_gr} g
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="text-center">
                    <div className="d-flex justify-content-center align-items-center">
                      <button
                        type="button"
                        className="btn btn-icon btn-white btn-sm"
                        onClick={() =>
                          updateLine(index, {
                            units: Math.max(1, line.units - 1),
                          })
                        }
                      >
                        <i className="fas fa-minus-circle" />
                      </button>
                      <span style={{ minWidth: '24px' }}>{line.units}</span>
                      <button
                        type="button"
                        className="btn btn-icon btn-white btn-sm"
                        onClick={() =>
                          updateLine(index, { units: line.units + 1 })
                        }
                      >
                        <i className="fas fa-plus-circle" />
                      </button>
                    </div>
                  </td>
                  <td className="text-end">
                    {(size?.weight_in_gr ?? 0) * line.units} g
                  </td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() =>
                        onChange(meals.filter((_line, i) => i !== index))
                      }
                    >
                      <span className="fa fa-trash" />
                    </button>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td colSpan={3} className="text-end fw-bold">
                {intl.formatMessage({ id: 'customerPlan.total' })}
              </td>
              <td className="text-end fw-bold text-primary">{totalGrams} g</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="btn btn-light-primary btn-sm"
        onClick={() => {
          const first = eligibleMeals[0];
          onChange([
            ...meals,
            {
              meal: first.id,
              meal_size: first.sizes[first.sizes.length - 1]?.id,
              units: 1,
            },
          ]);
        }}
      >
        <span className="fa fa-plus me-3" />
        {intl.formatMessage({ id: 'customerPlan.addMeal' })}
      </button>
    </div>
  );
}

function ResultValue({ label, value }: { label: string; value: string }) {
  return (
    <div className={clsx('col-md-2', 'mb-3')}>
      <div className="text-gray-700 fs-7">{label}</div>
      <div className="fs-3 fw-bold">{value}</div>
    </div>
  );
}
